import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// ===== API BASE (evita hardcodear localhost) =====
const API_BASE =
  (process.env.REACT_APP_API_URL &&
    process.env.REACT_APP_API_URL.replace(/\/+$/, '')) ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : window.location.origin);

// Función helper para obtener información del rol
const getRoleInfo = (rol) => {
  const roleMap = {
    admin: {
      emoji: '👑',
      label: 'Administrador',
      color: 'bg-purple-100 text-purple-800',
      description: 'Acceso completo al sistema'
    },
    logistica: {
      emoji: '📦',
      label: 'Logística',
      color: 'bg-blue-100 text-blue-800',
      description: 'Gestión de paquetes y envíos'
    },
    'mesa de control': {
      emoji: '🎛️',
      label: 'Mesa de Control',
      color: 'bg-green-100 text-green-800',
      description: 'Monitoreo y control operativo'
    },
    asesor: {
      emoji: '💼',
      label: 'Asesor',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Asesoría y atención al cliente'
    },
    // *** ROL ADMINISTRATIVO DEFINIDO ***
    administrativo: { // Nombre exacto como se usa en backend
      emoji: '📊', // Ícono sugerido
      label: 'Administración', // Etiqueta para UI
      color: 'bg-indigo-100 text-indigo-800',
      description: 'Gestión administrativa y cortes' // Descripción ajustada
    },
    // *** NUEVO ROL CONTABLE AÑADIDO ***
    contador: { // Nombre exacto como se usará en backend (minúscula consistente)
      emoji: '🧾', // Ícono sugerido
      label: 'Contador', // Etiqueta para UI
      color: 'bg-teal-100 text-teal-800',
      description: 'Acceso a cortes y pagos finalizados' // Descripción según requerimiento
    }
  };

  // Devuelve la información mapeada o una genérica si no se encuentra
  return roleMap[rol] || {
    emoji: '👤',
    label: rol || 'Usuario', // Mostrar el rol aunque no esté mapeado
    color: 'bg-gray-100 text-gray-800',
    description: 'Rol no definido explícitamente'
  };
};

// Componente de Modal de Formulario de Usuario
function UserFormModal({ user, onClose, onSave, currentUser, authToken }) {
  const [username, setUsername] = useState(user ? user.user : '');
  const [name, setName] = useState(user ? user.nombre : '');
  const [email, setEmail] = useState(user ? user.email : '');
  const [password, setPassword] = useState('');
  // *** Asegurar que el rol por defecto sea válido y existente ***
  const [role, setRole] = useState(user ? user.rol : 'logistica'); // logistica es un rol válido
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    if (!authToken) {
      setMessage('Error: Token de autenticación no disponible.');
      setLoading(false);
      return;
    }

    // Validación básica de campos
    if (!name.trim()) {
        setMessage('Error: El nombre completo es obligatorio.');
        setLoading(false);
        return;
    }
     if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
        setMessage('Error: Ingresa un correo electrónico válido.');
        setLoading(false);
        return;
    }
     if (!role) {
        setMessage('Error: Debes seleccionar un rol.');
        setLoading(false);
        return;
    }

    const userData = {
      nombre: name.trim(), // Usar trim para limpiar espacios
      email: email.trim(),
      rol: role,
    };

    // Solo incluir 'user' (username) si es un usuario nuevo
    if (!user) {
       if (!username.trim() || /\s/.test(username)) { // Validar username
         setMessage('Error: El nombre de usuario es obligatorio y no debe contener espacios.');
         setLoading(false);
         return;
       }
      userData.user = username.trim();
    }

    // Incluir contraseña solo si es usuario nuevo o si se escribió una nueva
    if (!user || password) {
      // Validación básica de contraseña (ej. longitud mínima)
      if (!password || password.length < 6) {
          setMessage('Error: La contraseña es obligatoria y debe tener al menos 6 caracteres.');
          setLoading(false);
          return;
      }
      userData.password = password; // No hacer trim a la contraseña
    }

    try {
      const url = user
        ? `${API_BASE}/api/users/${user.id}`
        : `${API_BASE}/api/users`;
      const method = user ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`Usuario ${user ? 'actualizado' : 'añadido'} exitosamente.`);
        setTimeout(() => {
          // Si se editó, pasar el usuario actualizado para actualizar el estado local sin recargar todo
          if (user) {
            const updatedUser = {
              id: user.id,
              user: user.user, // Username no cambia al editar
              nombre: name,
              email: email,
              rol: role
            };
            onSave(updatedUser); // Pasar el objeto actualizado
          } else {
            // Si se creó, onSave no recibe argumento, indicando recargar lista
            onSave();
          }
        }, 1500); // Dar tiempo para ver el mensaje
      } else {
        // Mostrar mensaje de error del backend
        setMessage(`Error: ${data.mensaje || 'Error desconocido al guardar usuario.'}`);
      }
    } catch (err) {
      setMessage('Error de conexión con el servidor. Intenta de nuevo.');
      console.error("Error en handleSubmit UserFormModal:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl relative overflow-hidden transform transition-all duration-300 scale-100 max-h-[95vh] overflow-y-auto">
        {/* Header del Modal */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 sm:px-6 py-3 sm:py-4 relative">
          <button
            onClick={onClose}
            className="absolute top-2 sm:top-4 right-2 sm:right-4 text-white hover:text-gray-200 transition-colors duration-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20"
            aria-label="Cerrar modal"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          <div className="flex items-center pr-8 sm:pr-12">
            <div className="bg-white bg-opacity-20 rounded-full p-2 sm:p-3 mr-3 sm:mr-4">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
              </svg>
            </div>
            <div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                {user ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <p className="text-blue-100 text-xs sm:text-sm">
                {user ? 'Actualiza la información del usuario' : 'Añade un nuevo usuario al sistema'}
              </p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className="p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            {/* Campo Username (solo para nuevos usuarios) */}
            {!user && (
              <div className="space-y-2">
                <label htmlFor="username" className="block text-sm font-semibold text-gray-700 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path></svg>
                  Nombre de Usuario *
                </label>
                <input
                  type="text"
                  id="username"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-white"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="Nombre de usuario único (sin espacios)"
                  pattern="^\S+$" // Evitar espacios
                  title="El nombre de usuario no debe contener espacios."
                />
              </div>
            )}

            {/* Campo Nombre Completo */}
            <div className="space-y-2">
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 flex items-center">
                 <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd"></path></svg>
                 Nombre Completo *
              </label>
              <input
                type="text"
                id="name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-white"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Nombre completo del usuario"
              />
            </div>

            {/* Campo Correo Electrónico */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 flex items-center">
                 <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path></svg>
                 Correo Electrónico *
              </label>
              <input
                type="email"
                id="email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-white"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="correo@ejemplo.com"
              />
            </div>

            {/* Campo Contraseña */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 flex items-center">
                 <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path></svg>
                 {user ? 'Nueva Contraseña (opcional)' : 'Contraseña *'}
              </label>
              <input
                type="password"
                id="password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-white"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={!user} // Requerido solo si es nuevo usuario
                placeholder={user ? 'Dejar vacío para no cambiar' : 'Mínimo 6 caracteres'}
                minLength={6} // Longitud mínima siempre
              />
              {user && (
                <p className="text-xs text-gray-500">Deja el campo vacío si no quieres cambiar la contraseña.</p>
              )}
            </div>

            {/* Campo Rol */}
            <div className="space-y-2">
              <label htmlFor="role" className="block text-sm font-semibold text-gray-700 flex items-center">
                 <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"></path></svg>
                 Rol en el Sistema *
              </label>
              <select
                id="role"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-white"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                required // El rol siempre es requerido
              >
                {/* *** OPCIONES ACTUALIZADAS Y AÑADIDO CONTABLE *** */}
                <option value="" disabled>Selecciona un rol...</option> {/* Opción por defecto */}
                <option value="admin">👑 Administrador</option>
                <option value="logistica">📦 Logística</option>
                <option value="asesor">💼 Asesor</option>
                <option value="mesa de control">🎛️ Mesa de Control</option>
                <option value="administrativo">📊 Administración</option> {/* Rol administrativo */}
                <option value="contador">🧾 Contador</option> {/* Nuevo rol contable */}
              </select>
            </div>

            {/* Mensaje de estado/error */}
            {message && (
              <div className={`p-4 rounded-lg text-center font-medium text-sm ${
                message.includes('Error')
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {message}
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-medium transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Guardando...
                  </>
                ) : (
                  `${user ? 'Actualizar' : 'Crear'} Usuario`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =========================================================
// Componente de Modal de Formulario de Paquetería
// =========================================================
function CarrierFormModal({ carrier, onClose, onSave, token }) {
  const [name, setName] = useState(carrier ? carrier.name : '');
  const [active, setActive] = useState(carrier ? carrier.active : true);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const knownCarriers = ['FedEx', 'PaqExp', 'Estafeta', 'DHL', 'Sendex', 'Afimex']; // Lista de sugerencias

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    if (!name.trim()) {
        setMessage('Error: El nombre de la paquetería es obligatorio.');
        setLoading(false);
        return;
    }

    const carrierData = {
      name: name.trim(),
      active: active
    };

    try {
      const url = carrier
        ? `${API_BASE}/api/carriers/${carrier.id}`
        : `${API_BASE}/api/carriers`;
      const method = carrier ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(carrierData),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`Paquetería ${carrier ? 'actualizada' : 'añadida'} exitosamente.`);
        setTimeout(() => {
          onSave(); // Indicar recargar la lista
        }, 1500);
      } else {
        setMessage(`Error: ${data.mensaje || 'Error desconocido al guardar paquetería.'}`);
      }
    } catch (err) {
      setMessage('Error de conexión con el servidor. Intenta de nuevo.');
      console.error("Error en handleSubmit CarrierFormModal:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full relative overflow-hidden transform transition-all duration-300 scale-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors duration-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20"
            aria-label="Cerrar modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 rounded-full p-3 mr-4">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"></path></svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">
                {carrier ? 'Editar Paquetería' : 'Nueva Paquetería'}
              </h3>
              <p className="text-green-100 text-sm">
                {carrier ? 'Actualiza la información de la paquetería' : 'Añade una nueva paquetería al sistema'}
              </p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Nombre Paquetería */}
            <div className="space-y-2">
              <label htmlFor="carrier-name" className="block text-sm font-semibold text-gray-700 flex items-center">
                 <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"></path></svg>
                 Nombre de la Paquetería *
              </label>
              <input
                type="text"
                id="carrier-name"
                list="known-carriers" // Sugerencias
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Ej: FedEx, DHL, Estafeta..."
              />
              <datalist id="known-carriers">
                {knownCarriers.map((carrierName) => (
                  <option key={carrierName} value={carrierName} />
                ))}
              </datalist>
              <p className="text-xs text-gray-500">Selecciona de la lista o escribe un nombre personalizado.</p>
            </div>

            {/* Estado Activo/Inactivo */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Estado de la Paquetería</label>
              <div className="flex items-center p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                <div className="flex items-center w-full">
                  <input
                    type="checkbox"
                    id="active-checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                    className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="active-checkbox" className="ml-3 cursor-pointer flex-grow">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${active ? 'text-green-700' : 'text-gray-600'}`}>
                        {active ? '✅ Paquetería Activa' : '❌ Paquetería Inactiva'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {active
                        ? 'Disponible para asignar a pedidos.'
                        : 'No aparecerá en las opciones de envío.'
                      }
                    </p>
                  </label>
                </div>
              </div>
            </div>

            {/* Mensaje de estado/error */}
            {message && (
              <div className={`p-4 rounded-lg text-center font-medium text-sm ${
                message.includes('Error')
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {message}
              </div>
            )}

            {/* Botones */}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg font-medium transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center"
              >
                {loading ? (
                  <>
                     <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                     Guardando...
                  </>
                ) : (
                  `${carrier ? 'Actualizar' : 'Crear'} Paquetería`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


// =========================================================
// Componente de Modal de Confirmación de Contraseña
// =========================================================
function PasswordConfirmationModal({ isOpen, onClose, onConfirm, token }) {
    const [password, setPassword] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // Función para verificar la contraseña del admin actual
    const verifyAdminPassword = async (pwd) => {
        try {
            const response = await fetch(`${API_BASE}/api/users/verify-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ password: pwd })
            });
            const data = await response.json();
            return response.ok && data.valid;
        } catch (error) {
            console.error('Error verificando contraseña:', error);
            setPasswordError('Error de red al verificar contraseña.');
            return false;
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setIsVerifying(true);
        setPasswordError('');

        if (!password.trim()) {
            setPasswordError('Por favor ingresa tu contraseña.');
            setIsVerifying(false);
            return;
        }

        const isValid = await verifyAdminPassword(password);

        if (isValid) {
            onConfirm(); // Ejecuta la acción de eliminación confirmada
            resetModal();
        } else {
            setPasswordError('Contraseña incorrecta. Intenta de nuevo.');
        }

        setIsVerifying(false);
    };

    const resetModal = () => {
        setPassword('');
        setPasswordError('');
        setIsVerifying(false);
        onClose(); // Cierra el modal
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 text-center">
                {/* Fondo Oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75 backdrop-blur-sm"></div>
                </div>

                {/* Contenido Modal */}
                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div className="sm:flex sm:items-start">
                        {/* Icono de Advertencia */}
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            {/* Título */}
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                                Confirmar Acción
                            </h3>
                            {/* Descripción */}
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 mb-4">
                                    Por seguridad, ingresa tu contraseña de administrador para confirmar esta acción.
                                </p>
                                {/* Formulario Contraseña */}
                                <form onSubmit={handlePasswordSubmit}>
                                    <div className="mb-4">
                                        <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-2 sr-only">
                                            Contraseña de Administrador:
                                        </label>
                                        <input
                                            type="password"
                                            id="admin-password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-400"
                                            placeholder="Ingresa tu contraseña"
                                            required
                                            autoFocus
                                        />
                                        {/* Error Contraseña */}
                                        {passwordError && (
                                            <p className="mt-2 text-sm text-red-600" id="password-error">{passwordError}</p>
                                        )}
                                    </div>
                                    {/* Botones (dentro del form para submit) */}
                                     <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                                        <button
                                            type="submit" // Cambiado a submit
                                            disabled={isVerifying}
                                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isVerifying ? 'Verificando...' : 'Confirmar'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={resetModal} // Usar resetModal para cancelar
                                            disabled={isVerifying}
                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Cancelar
                                        </button>
                                     </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


// =========================================================
// Componente Principal del Módulo
// =========================================================
function UserManagementModule({ initialTab = 'users' }) {
  const { user, token } = useAuth(); // Usuario actual logueado
  const [users, setUsers] = useState([]); // Lista de todos los usuarios
  const [carriers, setCarriers] = useState([]); // Lista de paqueterías
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Estados para el modal de usuario
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // Usuario a editar

  // Estados para el modal de paquetería
  const [isCarrierModalOpen, setIsCarrierModalOpen] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(null); // Paquetería a editar

  // Estados para confirmación de contraseña al eliminar
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null); // { action: 'deleteUser' | 'deleteCarrier', data: id }

  // Calcula estadísticas de usuarios por rol
  const getUserStats = () => {
    const total = users.length;
    const byRole = users.reduce((acc, u) => {
      acc[u.rol] = (acc[u.rol] || 0) + 1;
      return acc;
    }, {});

    // Retorna conteos para todos los roles definidos, incluyendo los nuevos
    return {
      total,
      admin: byRole.admin || 0,
      logistica: byRole.logistica || 0,
      asesor: byRole.asesor || 0,
      'mesa de control': byRole['mesa de control'] || 0,
      administrativo: byRole.administrativo || 0,
      contador: byRole.contador || 0, // Incluye el rol contable
    };
  };

  // Calcula estadísticas de paqueterías
  const getCarrierStats = () => {
    const total = carriers.length;
    const active = carriers.filter(c => c.active).length;
    const inactive = total - active;
    return { total, active, inactive };
  };

  // --- Funciones API ---
  const fetchUsers = useCallback(async () => {
    console.log('UserManagementModule: fetchUsers llamado.');
    setLoading(true);
    setError(null);
    try {
      if (!user || user.rol !== 'admin' || !token) {
        // No lanzar error aquí, solo prevenir la llamada y poner mensaje en UI
        console.warn('UserManagementModule: Acceso denegado o token no disponible para fetchUsers.');
        setError('Acceso denegado o token no disponible.');
        setLoading(false); // Importante: terminar carga
        return; // Detener ejecución
      }
      console.log(`UserManagementModule: Realizando fetch a ${API_BASE}/api/users con token.`);
      const response = await fetch(`${API_BASE}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.mensaje || 'Error al cargar usuarios.');
      setUsers(data.users || []); // Asegurarse que sea un array
      console.log('UserManagementModule: Usuarios cargados:', data.users);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [user, token]); // Dependencias: user y token

  const fetchCarriers = useCallback(async () => {
     console.log('UserManagementModule: fetchCarriers llamado.');
     setLoading(true);
     setError(null);
     try {
       if (!user || user.rol !== 'admin' || !token) {
         // No lanzar error aquí
         console.warn('UserManagementModule: Acceso denegado o token no disponible para fetchCarriers.');
         setError('Acceso denegado o token no disponible.');
         setLoading(false); // Terminar carga
         return; // Detener
       }
       console.log(`UserManagementModule: Realizando fetch a ${API_BASE}/api/carriers con token.`);
       const response = await fetch(`${API_BASE}/api/carriers`, {
         headers: { 'Authorization': `Bearer ${token}` }
       });
       const data = await response.json();
       if (!response.ok) throw new Error(data.mensaje || 'Error al cargar paqueterías.');
       setCarriers(data || []); // Asegurarse que sea un array
       console.log('UserManagementModule: Paqueterías cargadas:', data);
     } catch (err) {
       setError(err.message);
       console.error('Error fetching carriers:', err);
     } finally {
       setLoading(false);
     }
  }, [user, token]); // Dependencias: user y token

  // --- Manejadores de Eventos ---

  // Abre el modal de confirmación de contraseña y guarda la acción pendiente
  const handlePasswordConfirmation = (action, data) => {
    setDeleteAction({ action, data }); // Guarda qué se quiere eliminar y con qué ID
    setIsPasswordModalOpen(true); // Abre el modal de contraseña
  };

  // Función interna para eliminar usuario (llamada después de confirmar contraseña)
  const originalDeleteUser = async (userId) => {
     console.log(`Intentando eliminar usuario ID: ${userId}`);
     // Mostrar feedback visual mientras se elimina
     // Idealmente, se podría deshabilitar el botón o mostrar un spinner en la fila
     try {
       const response = await fetch(`${API_BASE}/api/users/${userId}`, {
         method: 'DELETE',
         headers: { 'Authorization': `Bearer ${token}` }
       });
       // Verificar si la respuesta es JSON antes de parsear
       const contentType = response.headers.get("content-type");
       let data = {};
       if (contentType && contentType.indexOf("application/json") !== -1) {
            data = await response.json();
       } else {
           // Si no es JSON, leer como texto
           const textData = await response.text();
           console.warn("Respuesta no JSON al eliminar usuario:", textData);
           if (!response.ok) throw new Error(textData || `Error ${response.status} al eliminar`);
       }

       if (!response.ok) throw new Error(data.mensaje || 'Error desconocido al eliminar');

       console.log('Usuario eliminado exitosamente.');
       // Actualizar la lista localmente eliminando el usuario
       setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
       // Opcional: mostrar notificación de éxito (ej. con react-toastify)
       // toast.success('Usuario eliminado.');
     } catch (err) {
       console.error('Error de red al eliminar usuario:', err);
       alert(`Error al eliminar usuario: ${err.message}`); // Mostrar error al usuario
     }
     // Quitar feedback visual de eliminación
  };

  // Función interna para eliminar paquetería (llamada después de confirmar contraseña)
  const originalDeleteCarrier = async (carrierId) => {
     console.log(`Intentando eliminar paquetería ID: ${carrierId}`);
     // Mostrar feedback visual
     try {
       const response = await fetch(`${API_BASE}/api/carriers/${carrierId}`, {
         method: 'DELETE',
         headers: { 'Authorization': `Bearer ${token}` }
       });
       // Verificar si es JSON
        const contentType = response.headers.get("content-type");
        let data = {};
        if (contentType && contentType.indexOf("application/json") !== -1) {
             data = await response.json();
        } else {
            const textData = await response.text();
            console.warn("Respuesta no JSON al eliminar paquetería:", textData);
            if (!response.ok) throw new Error(textData || `Error ${response.status} al eliminar`);
        }

       if (!response.ok) throw new Error(data.mensaje || 'Error desconocido al eliminar');

       console.log('Paquetería eliminada exitosamente.');
       // Actualizar la lista localmente
       setCarriers(prevCarriers => prevCarriers.filter(c => c.id !== carrierId));
       // Opcional: notificación de éxito
       // toast.success('Paquetería eliminada.');
     } catch (err) {
       console.error('Error de red al eliminar paquetería:', err);
       alert(`Error al eliminar paquetería: ${err.message}`);
     }
     // Quitar feedback visual
  };

  // Carga inicial de datos según la pestaña activa
  useEffect(() => {
    // Solo cargar si es admin y tiene token
    if (user && user.rol === 'admin' && token) {
      setLoading(true); // Indicar carga al cambiar de tab o al montar
      setError(null);    // Limpiar errores previos
      if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'carriers') {
        fetchCarriers();
      }
    } else if (!loading && (!user || user.rol !== 'admin')) {
        // Si ya terminó la carga inicial y no es admin, establecer error
        setError('Acceso denegado: Solo administradores pueden ver esta sección.');
        setUsers([]); // Limpiar datos
        setCarriers([]);
        setLoading(false); // Asegurarse que loading sea false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user, token]); // fetchUsers/Carriers ya no están aquí para evitar llamadas dobles


  // Actualiza un usuario en la lista localmente después de editarlo
  const updateUserInState = (updatedUser) => {
     setUsers(prevUsers =>
       prevUsers.map(u => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u))
     );
  };

  // --- Manejadores de Modales ---
  const openAddModal = () => { setSelectedUser(null); setIsModalOpen(true); };
  const openEditModal = (userToEdit) => { setSelectedUser(userToEdit); setIsModalOpen(true); };
  // Cierra el modal de usuario y decide si recargar la lista
  const closeModal = (updatedUser = null) => {
    setIsModalOpen(false);
    setSelectedUser(null);
    if (updatedUser) { // Si se editó, actualizar localmente
      updateUserInState(updatedUser);
    } else if (updatedUser === undefined) { // Si se creó (onSave no recibe argumento), recargar
      fetchUsers(); // Volver a pedir la lista completa
    }
    // Si updatedUser es null (cancelar), no hacer nada
  };

  const openAddCarrierModal = () => { setSelectedCarrier(null); setIsCarrierModalOpen(true); };
  const openEditCarrierModal = (carrierToEdit) => { setSelectedCarrier(carrierToEdit); setIsCarrierModalOpen(true); };
  // Cierra el modal de paquetería y recarga la lista
  const closeCarrierModal = () => {
     setIsCarrierModalOpen(false);
     setSelectedCarrier(null);
     fetchCarriers(); // Siempre recargar al cerrar modal de paqueterías
  };

  // Inicia el proceso de eliminación (abre modal de contraseña)
  const handleDeleteUser = (userId) => { handlePasswordConfirmation('deleteUser', userId); };
  const handleDeleteCarrier = (carrierId) => { handlePasswordConfirmation('deleteCarrier', carrierId); };

  // Función que se pasa al modal de contraseña para ejecutar la acción
   const executeDeleteAction = () => {
      if (deleteAction?.action === 'deleteUser') {
         originalDeleteUser(deleteAction.data);
      } else if (deleteAction?.action === 'deleteCarrier') {
         originalDeleteCarrier(deleteAction.data);
      }
      setDeleteAction(null); // Limpiar la acción pendiente
   };


  // --- Renderizado ---

  // Vista si no es admin (o si está cargando el estado inicial del usuario)
  if (loading && !user) { // Si está cargando y aún no hay info de usuario
      return <div className="p-8 text-center text-gray-500">Verificando acceso...</div>;
  }
  if (!user || user.rol !== 'admin') {
    // Renderiza el mensaje de error si existe (establecido en useEffect)
    return (
      <div className="flex flex-col items-center justify-center p-6 bg-red-50 min-h-[300px] rounded-lg shadow-md border border-red-200 text-red-800">
        <h2 className="text-2xl font-bold mb-4">Panel de Administración</h2>
        <p className="text-xl mb-8 text-center">
           Acceso Denegado
        </p>
        <p className="text-center">{error || 'Solo los usuarios administradores pueden acceder a esta sección.'}</p>
      </div>
    );
  }

  // Si es admin, renderizar el módulo
  const userStats = getUserStats();
  const carrierStats = getCarrierStats();

  return (
    <div className="min-h-screen bg-gray-50 pb-12"> {/* Añadir padding-bottom */}
      {/* Encabezado */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-6 sticky top-0 z-20"> {/* Hacer sticky */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-5"> {/* Reducir padding vertical */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Panel de Administración</h2> {/* Reducir tamaño texto */}
              <p className="text-gray-600 mt-1 text-sm">Gestiona usuarios y paqueterías del sistema</p> {/* Reducir tamaño texto */}
            </div>
            {/* Icono decorativo */}
             <div className="flex items-center space-x-4">
               <div className="bg-blue-100 p-3 rounded-full shadow-inner border border-blue-200">
                 <span className="text-blue-600 text-2xl">⚙️</span>
               </div>
             </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Contenedor principal con Pestañas */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-6 overflow-hidden"> {/* Mejorar sombra y overflow */}
          {/* Navegación de Pestañas */}
          <div className="border-b border-gray-200 bg-gray-50"> {/* Fondo ligero para tabs */}
            <nav className="-mb-px flex space-x-6 px-6" aria-label="Tabs"> {/* Ajustar espaciado y padding */}
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-3 border-b-2 font-semibold text-sm flex items-center ${ /* Aumentar font-weight */
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-1 rounded-t-md`} /* Añadir focus y offset */
                aria-current={activeTab === 'users' ? 'page' : undefined}
              >
                <span className="mr-2 text-lg">👥</span> {/* Aumentar tamaño icono */}
                Usuarios
              </button>
              <button
                onClick={() => setActiveTab('carriers')}
                className={`py-4 px-3 border-b-2 font-semibold text-sm flex items-center ${
                  activeTab === 'carriers'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-1 rounded-t-md`}
                aria-current={activeTab === 'carriers' ? 'page' : undefined}
              >
                <span className="mr-2 text-lg">📦</span>
                Paqueterías
              </button>
            </nav>
          </div>

          {/* Contenido de la Pestaña */}
          <div className="p-6">
            {/* --- PESTAÑA USUARIOS --- */}
            {activeTab === 'users' && (
              <div>
                {/* Tarjetas de Estadísticas */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6"> {/* Ajustar columnas */}
                   {/* Tarjeta Total */}
                   <StatCard icon="👥" label="Total Usuarios" value={loading ? '...' : userStats.total} color="blue" />
                   {/* Tarjeta Admin */}
                   <StatCard icon="👑" label="Admin" value={loading ? '...' : userStats.admin} color="purple" />
                   {/* Tarjeta Logística */}
                   <StatCard icon="📦" label="Logística" value={loading ? '...' : userStats.logistica} color="sky" />
                   {/* Tarjeta Asesores */}
                   <StatCard icon="💼" label="Asesores" value={loading ? '...' : userStats.asesor} color="amber" />
                   {/* Tarjeta Mesa Control */}
                   <StatCard icon="🎛️" label="Mesa Control" value={loading ? '...' : userStats['mesa de control']} color="emerald" />
                   {/* Tarjeta Administración */}
                   <StatCard icon="📊" label="Administración" value={loading ? '...' : userStats.administrativo} color="indigo" />
                   {/* Tarjeta Contador (Añadida) */}
                   <StatCard icon="🧾" label="Contador" value={loading ? '...' : userStats.contador} color="teal" />
                </div>

                 {/* Encabezado y Botón Añadir Usuario */}
                 <div className="flex flex-col sm:flex-row justify-between items-center mb-5 gap-3">
                   <h3 className="text-xl font-semibold text-gray-800">Lista de Usuarios</h3>
                   <button
                     onClick={openAddModal}
                     className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-2.5 px-5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 transition duration-200 flex items-center shadow-md hover:shadow-lg transform hover:-translate-y-0.5" /* Mejorar botón */
                   >
                     <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                     Añadir Usuario
                   </button>
                 </div>

                {/* Estados Carga/Error/Vacío/Tabla */}
                 {loading ? (
                    <LoadingSpinner message="Cargando usuarios..." />
                 ) : error ? (
                    <ErrorMessage message={error} onRetry={fetchUsers} />
                 ) : users.length === 0 ? (
                    <EmptyState message="No se encontraron usuarios." subMessage="Añade el primer usuario al sistema." icon="👥" />
                 ) : (
                    <UsersTable users={users} onEdit={openEditModal} onDelete={handleDeleteUser} />
                 )}
              </div>
            )}

            {/* --- PESTAÑA PAQUETERÍAS --- */}
            {activeTab === 'carriers' && (
               <div>
                  {/* Tarjetas Estadísticas */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                     <StatCard icon="📦" label="Total Paqueterías" value={loading ? '...' : carrierStats.total} color="green" />
                     <StatCard icon="✅" label="Activas" value={loading ? '...' : carrierStats.active} color="cyan" />
                     <StatCard icon="❌" label="Inactivas" value={loading ? '...' : carrierStats.inactive} color="orange" />
                  </div>

                  {/* Encabezado y Botón Añadir */}
                  <div className="flex flex-col sm:flex-row justify-between items-center mb-5 gap-3">
                    <h3 className="text-xl font-semibold text-gray-800">Lista de Paqueterías</h3>
                    <button
                      onClick={openAddCarrierModal}
                      className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-2.5 px-5 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-50 transition duration-200 flex items-center shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                      Añadir Paquetería
                    </button>
                  </div>

                  {/* Estados Carga/Error/Vacío/Grid */}
                  {loading ? (
                     <LoadingSpinner message="Cargando paqueterías..." />
                  ) : error ? (
                     <ErrorMessage message={error} onRetry={fetchCarriers} />
                  ) : carriers.length === 0 ? (
                     <EmptyState message="No se encontraron paqueterías." subMessage="Añade la primera paquetería al sistema." icon="📦" />
                  ) : (
                     <CarriersGrid carriers={carriers} onEdit={openEditCarrierModal} onDelete={handleDeleteCarrier} />
                  )}
               </div>
            )}
          </div>
        </div>

        {/* --- Renderizado de Modales --- */}
        {isModalOpen && (
          <UserFormModal
            user={selectedUser}
            onClose={closeModal}
            onSave={closeModal}
            currentUser={user}
            authToken={token}
          />
        )}

        {isCarrierModalOpen && (
          <CarrierFormModal
            carrier={selectedCarrier}
            onClose={closeCarrierModal}
            onSave={closeCarrierModal} // onSave ahora solo indica recargar
            token={token}
          />
        )}

        {/* Modal de confirmación de contraseña */}
        <PasswordConfirmationModal
           isOpen={isPasswordModalOpen}
           onClose={() => setIsPasswordModalOpen(false)}
           onConfirm={executeDeleteAction} // Pasa la función que ejecutará la acción
           token={token}
        />

      </div> {/* Fin max-w-7xl */}
    </div> // Fin contenedor principal
  );
}

// =========================================================
// Componentes Auxiliares UI (Reutilizables)
// =========================================================

// Tarjeta de Estadística Genérica
function StatCard({ icon, label, value, color }) {
    const gradients = {
        blue: 'from-blue-500 to-blue-600', purple: 'from-purple-500 to-purple-600',
        sky: 'from-sky-500 to-sky-600', amber: 'from-amber-500 to-amber-600',
        emerald: 'from-emerald-500 to-emerald-600', indigo: 'from-indigo-500 to-indigo-600',
        teal: 'from-teal-500 to-teal-600', green: 'from-green-500 to-green-600',
        cyan: 'from-cyan-500 to-cyan-600', orange: 'from-orange-500 to-orange-600',
    };
    const bgOpacity = {
        blue: 'bg-blue-400', purple: 'bg-purple-400', sky: 'bg-sky-400',
        amber: 'bg-amber-400', emerald: 'bg-emerald-400', indigo: 'bg-indigo-400',
        teal: 'bg-teal-400', green: 'bg-green-400', cyan: 'bg-cyan-400', orange: 'bg-orange-400',
    };
    const textColors = {
        blue: 'text-blue-100', purple: 'text-purple-100', sky: 'text-sky-100',
        amber: 'text-amber-100', emerald: 'text-emerald-100', indigo: 'text-indigo-100',
        teal: 'text-teal-100', green: 'text-green-100', cyan: 'text-cyan-100', orange: 'text-orange-100',
    }

    return (
        <div className={`bg-gradient-to-br ${gradients[color] || gradients.blue} rounded-lg p-4 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className={`${textColors[color] || textColors.blue} text-sm font-medium`}>{label}</p>
                    <p className="text-2xl font-bold">{value}</p>
                </div>
                <div className={`${bgOpacity[color] || bgOpacity.blue} bg-opacity-30 rounded-full p-3`}>
                    <span className="text-2xl">{icon}</span>
                </div>
            </div>
        </div>
    );
}

// Spinner de Carga
function LoadingSpinner({ message = "Cargando..." }) {
    return (
        <div className="text-center py-10">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-3"></div>
            <p className="text-gray-600">{message}</p>
        </div>
    );
}

// Mensaje de Error con Botón Reintentar
function ErrorMessage({ message = "Ocurrió un error.", onRetry }) {
    return (
        <div className="text-center py-10 bg-red-50 border border-red-200 rounded-lg shadow-sm">
            <p className="text-red-700 font-semibold text-lg">Error</p>
            <p className="text-red-600 text-sm mt-1">{message}</p>
            {onRetry && (
                <button onClick={onRetry} className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50">
                    Reintentar
                </button>
            )}
        </div>
    );
}

// Mensaje de Estado Vacío
function EmptyState({ message, subMessage, icon }) {
    return (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center shadow-inner">
                <span className="text-5xl text-gray-400">{icon}</span>
            </div>
            <p className="text-gray-700 text-lg font-semibold mb-1">{message}</p>
            <p className="text-gray-500 text-sm">{subMessage}</p>
        </div>
    );
}

// Tabla de Usuarios
function UsersTable({ users, onEdit, onDelete }) {
    return (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    {/* Encabezado */}
                    <thead className="bg-gradient-to-r from-gray-100 to-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"><div className="flex items-center"><span className="mr-1.5">#️⃣</span>ID</div></th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"><div className="flex items-center"><span className="mr-1.5">👤</span>Usuario</div></th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"><div className="flex items-center"><span className="mr-1.5">📝</span>Nombre</div></th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"><div className="flex items-center"><span className="mr-1.5">📧</span>Email</div></th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"><div className="flex items-center"><span className="mr-1.5">🏷️</span>Rol</div></th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider"><div className="flex items-center justify-end"><span className="mr-1.5">⚙️</span>Acciones</div></th>
                        </tr>
                    </thead>
                    {/* Cuerpo */}
                    <tbody className="bg-white divide-y divide-gray-100">
                        {users.map((u, index) => {
                            const roleInfo = getRoleInfo(u.rol);
                            return (
                                <tr key={u.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-colors duration-150">
                                    {/* ID */}
                                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-700">#{u.id}</div></td>
                                    {/* Usuario */}
                                    <td className="px-6 py-4 whitespace-nowrap"><div className="flex items-center"><div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm border border-white"><span className="text-white font-bold text-sm">{u.user.charAt(0).toUpperCase()}</span></div><div className="ml-4"><div className="text-sm font-semibold text-gray-900">{u.user}</div></div></div></td>
                                    {/* Nombre */}
                                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-800">{u.nombre}</div></td>
                                    {/* Email */}
                                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-blue-600 hover:underline">{u.email}</div></td>
                                    {/* Rol */}
                                    <td className="px-6 py-4 whitespace-nowrap"><span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm border ${roleInfo.color} border-opacity-40`} style={{ borderColor: roleInfo.color.replace('bg-', 'border-').replace('-100', '-300') }}><span className="mr-1.5 text-base">{roleInfo.emoji}</span>{roleInfo.label}</span><div className="text-xs text-gray-500 mt-1 max-w-[150px] truncate" title={roleInfo.description}>{roleInfo.description}</div></td>
                                    {/* Acciones */}
                                    <td className="px-6 py-4 whitespace-nowrap text-right"><div className="flex justify-end items-center space-x-2"><button onClick={() => onEdit(u)} className="p-2 rounded-full text-blue-600 hover:bg-blue-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50" title="Editar"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg></button><button onClick={() => onDelete(u.id)} className="p-2 rounded-full text-red-600 hover:bg-red-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50" title="Eliminar"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button></div></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// Grid de Paqueterías
function CarriersGrid({ carriers, onEdit, onDelete }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {carriers.map((carrier) => (
                <div key={carrier.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-200 overflow-hidden flex flex-col">
                    {/* Encabezado Card */}
                    <div className={`px-4 py-3 border-b border-gray-200 ${carrier.active ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-red-50 to-rose-50'}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className="text-lg font-semibold text-gray-900">{carrier.name}</h4>
                                <p className="text-xs text-gray-500">ID: {carrier.id}</p>
                            </div>
                            {/* Badge Estado */}
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold shadow-inner border ${ carrier.active ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200' }`}>
                                {carrier.active ? 'ACTIVA' : 'INACTIVA'}
                            </span>
                        </div>
                    </div>
                    {/* Cuerpo Card */}
                    <div className="px-4 py-4 space-y-3 flex-grow flex flex-col justify-between">
                        {/* Info Adicional */}
                        <div className="space-y-1.5 text-xs text-gray-600">
                            <div className="flex justify-between items-center"><span className="font-medium text-gray-500">Creada:</span><span className="bg-gray-100 px-2 py-0.5 rounded text-gray-700 font-mono">{new Date(carrier.created_at).toLocaleDateString()}</span></div>
                            {carrier.updated_at && <div className="flex justify-between items-center"><span className="font-medium text-gray-500">Actualizada:</span><span className="bg-gray-100 px-2 py-0.5 rounded text-gray-700 font-mono">{new Date(carrier.updated_at).toLocaleDateString()}</span></div>}
                            <div className="flex justify-between items-center"><span className="font-medium text-gray-500">Estado:</span><span className={`font-semibold ${carrier.active ? 'text-green-600' : 'text-red-600'}`}>{carrier.active ? 'Operativa' : 'Suspendida'}</span></div>
                        </div>
                        {/* Botones */}
                        <div className="flex justify-end space-x-2 pt-2">
                             <button onClick={() => onEdit(carrier)} className="p-2 rounded-full text-blue-600 hover:bg-blue-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50" title="Editar"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg></button>
                             <button onClick={() => onDelete(carrier.id)} className="p-2 rounded-full text-red-600 hover:bg-red-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50" title="Eliminar"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default UserManagementModule;

